// PREPARACIÓN FÍSICA: un número, no un gráfico. Y alumnos, no filas.
//
// El acceso a PF se guarda como un crédito de `user_credits` con
// `credit_type = 'gym'`. Medido en producción el 4-oct-2026:
//
//   | filas `gym`                    | 3 |
//   | ALUMNOS distintos              | 2 |
//   | regaladas a mano (`is_gift`)   | 3 |
//   | productos de PF en el catálogo | 0 de 13 |
//
// El gráfico de barras que había se titulaba «alumnos inscritos por mes» y
// pintaba **3**: contaba filas. Son dos alumnos —Adidas y Nike, las dos cuentas
// de prueba— y uno de ellos tiene dos créditos. Un número que dice «alumnos» y
// cuenta otra cosa es el mismo fallo que el de las suscripciones.
//
// Y NO HAY PRODUCTO DE PF EN EL CATÁLOGO: ninguno de los 13 productos entrega
// acceso al gimnasio, así que los tres créditos existentes se concedieron a
// mano. Por eso el número correcto hoy es CERO este mes, y por eso tiene que
// ser un KPI y no un gráfico: una barra a cero seis veces no dice nada, y un
// número con su «mes anterior» sí.

/** El `credit_type` que da acceso a Preparación Física. */
export const CREDITO_PF = 'gym';

export interface CreditoDePF {
  user_id: string | null;
  purchase_date: string;
}

export interface PuntoMensual {
  mes: string;
  alumnos: number;
}

/**
 * Alumnos distintos que entraron en PF dentro de `[desde, hasta)`.
 *
 * Medio abierto a propósito: con `<=` sobre el fin de mes, un crédito sellado
 * a las 23:59:59.999 del día 30 cuenta en los dos meses.
 */
export function alumnosDePFEntre(
  creditos: readonly CreditoDePF[],
  desde: Date,
  hasta: Date
): number {
  const d = desde.getTime();
  const h = hasta.getTime();
  const gente = new Set<string>();
  for (const c of creditos) {
    if (c.user_id == null) continue;
    const t = new Date(c.purchase_date).getTime();
    if (t >= d && t < h) gente.add(c.user_id);
  }
  return gente.size;
}

/**
 * La serie mensual, un punto por mes.
 *
 * Los meses llegan como fechas y se recorren por intervalo, no agrupando por
 * una etiqueta de texto: con `format(m, 'MMM yy')` como clave, dos meses del
 * mismo nombre en años distintos caen en el mismo cubo.
 *
 * ⚠️ El alumno se cuenta en CADA mes en que entró, así que la suma de la serie
 * puede ser mayor que los alumnos distintos del periodo. Es lo que se quiere de
 * una serie mensual, pero no se puede sumar para sacar un total.
 */
export function seriePF(
  creditos: readonly CreditoDePF[],
  meses: readonly Date[],
  etiqueta: (m: Date) => string,
  siguienteMes: (m: Date) => Date
): PuntoMensual[] {
  return meses.map((m) => ({
    mes: etiqueta(m),
    alumnos: alumnosDePFEntre(creditos, m, siguienteMes(m)),
  }));
}
